// /pages/HR/components/charts/ChartCard.js
import React, { useState, useCallback } from 'react';
import '../charts/ChartCard.css';

/**
 * Chart Card Container Component
 * Wraps charts with title, actions, and consistent styling
 */
const ChartCard = React.memo(({
  title = '',
  subtitle = '',
  children,
  actions = [],
  className = '',
  loading = false,
  error = null,
  fullscreen = false,
  onFullscreen = null,
  onRefresh = null,
  onExport = null,
  showRefreshButton = true,
  showFullscreenButton = true,
  showExportButton = true,
  headerRight = null,
  footer = null,
  height = 'auto',
  minHeight = 350,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(fullscreen);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Handle fullscreen toggle
  const handleFullscreen = useCallback(() => {
    const newFullscreenState = !isFullscreen;
    setIsFullscreen(newFullscreenState);
    if (onFullscreen) {
      onFullscreen(newFullscreenState);
    }
  }, [isFullscreen, onFullscreen]);

  // Handle refresh
  const handleRefresh = useCallback(async () => {
    if (onRefresh && !isRefreshing) {
      setIsRefreshing(true);
      try {
        await onRefresh();
      } catch (error) {
        console.error('Error refreshing chart:', error);
      } finally {
        setIsRefreshing(false);
      }
    }
  }, [onRefresh, isRefreshing]);

  // Handle export
  const handleExport = useCallback(async (format) => {
    if (onExport && !isExporting) {
      setIsExporting(true);
      try {
        await onExport(format);
      } catch (error) {
        console.error('Error exporting chart:', error);
      } finally {
        setIsExporting(false);
      }
    }
  }, [onExport, isExporting]);

  return (
    <div 
      className={`chart-card ${className} ${isFullscreen ? 'fullscreen' : ''} ${loading ? 'loading' : ''} ${error ? 'has-error' : ''}`}
      style={{ 
        height: height === 'auto' ? 'auto' : height,
        minHeight: minHeight 
      }}
    >
      {/* Card Header */}
      <div className="chart-card-header">
        <div className="chart-card-title-section">
          {title && (
            <h3 className="chart-card-title">
              {title}
              {subtitle && (
                <span className="chart-card-subtitle">{subtitle}</span>
              )}
            </h3>
          )}
        </div>

        <div className="chart-card-actions">
          {/* Custom header content */}
          {headerRight}

          {/* Custom action buttons */}
          {actions.map((action, index) => (
            <button
              key={index}
              className={`chart-action-button ${action.className || ''}`}
              onClick={action.onClick}
              disabled={action.disabled || loading}
              title={action.tooltip}
            >
              {action.icon && <span className="material-icons">{action.icon}</span>}
              {action.label && <span className="action-label">{action.label}</span>}
            </button>
          ))}

          {/* Refresh button */}
          {showRefreshButton && onRefresh && (
            <button
              className={`chart-action-button refresh-button ${isRefreshing ? 'refreshing' : ''}`}
              onClick={handleRefresh}
              disabled={isRefreshing || loading}
              title="Refresh data"
            >
              <span className="material-icons">refresh</span>
            </button>
          )}

          {/* Export button with dropdown */}
          {showExportButton && onExport && (
            <div className="export-dropdown">
              <button
                className="chart-action-button export-button"
                disabled={isExporting || loading}
                title="Export chart"
              >
                <span className="material-icons">download</span>
              </button>
              <div className="export-menu">
                <button 
                  className="export-option"
                  onClick={() => handleExport('png')}
                >
                  Export as PNG
                </button>
                <button 
                  className="export-option"
                  onClick={() => handleExport('csv')}
                >
                  Export as CSV
                </button>
                <button 
                  className="export-option"
                  onClick={() => handleExport('pdf')}
                >
                  Export as PDF
                </button>
              </div>
            </div>
          )}

          {/* Fullscreen button */}
          {showFullscreenButton && (
            <button
              className="chart-action-button fullscreen-button"
              onClick={handleFullscreen}
              disabled={loading}
              title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            >
              <span className="material-icons">
                {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Card Body */}
      <div className="chart-card-body">
        {/* Loading Overlay */}
        {loading && (
          <div className="chart-loading-overlay">
            <div className="chart-spinner"></div>
            <span className="loading-text">Loading chart data...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="chart-error-state">
            <span className="error-icon">⚠️</span>
            <p className="error-message">{error}</p>
            {onRefresh && (
              <button 
                className="retry-button"
                onClick={handleRefresh}
              >
                Try Again
              </button>
            )}
          </div>
        )}

        {/* Chart Content */}
        {!error && children}
      </div>

      {/* Card Footer */}
      {footer && (
        <div className="chart-card-footer">
          {footer}
        </div>
      )}

      {/* Status Indicators */}
      {isRefreshing && (
        <div className="chart-status-indicator refreshing">
          <span className="status-dot"></span>
          Refreshing...
        </div>
      )}

      {isExporting && (
        <div className="chart-status-indicator exporting">
          <span className="status-dot"></span>
          Exporting...
        </div>
      )}
    </div>
  );
});

ChartCard.displayName = 'ChartCard';

export default ChartCard;